/**
 * Colours the design system still needs, that `semantic.colour.*` cannot yet
 * express.
 *
 * Every value here is a literal lifted verbatim out of the legacy `palette`
 * layer, so nothing renders differently — the point is to get the last readers
 * off `palette` so it can be deleted, and to collect the outstanding design
 * questions in one file instead of scattering them across 20 components.
 *
 * Each entry is a Figma ask. When a real token lands, delete the constant and
 * point its consumers at `semantic.colour.*`; the type errors will list them.
 *
 * These are plain literals rather than CSS custom properties, so unlike the
 * mapped tokens they cannot be themed. Nothing in the repo overrides the
 * `--palette-*` properties, so that is not a regression today. RWR-18028.
 */

/**
 * The focus ring, on 14 sites — every focusable control in the design system.
 *
 * iOS system blue at 20%. It is not on the brand ramp at all: the nearest
 * semantic token is 108 away (RGB euclidean), which is a different colour rather
 * than a rounding difference. Preserving the value needs a token that exists
 * only to hold it; taking the role-correct route recolours every focus ring in
 * the product. That is a design decision, not a mapping — the single largest
 * outstanding item.
 */
export const FOCUS_RING = 'rgba(0,122,255,0.2)';

/** Visited external links in `TextLink`. No semantic token; nearest is 67 away. */
export const TEXT_LINK_VISITED = '#3C3391';

/**
 * The bottom border of a disabled table row.
 *
 * The disabled row is painted at `opacity: 0.25`, and the original comment at
 * the call site says this colour was reverse-engineered to survive that — a
 * pre-composited border, picked so the row reads correctly once faded.
 *
 * **That derivation is now stale.** `#7e8190` at 25% over white is `#dfe0e3`,
 * which is not today's `surface.neutral.border` (`#d2d8ea`); the border token has
 * moved since. So this is not "the standard border, pre-composited" any more —
 * it is just a value someone once computed. The real fix is for the row to stop
 * fading its own border and let the border token apply directly, which is a
 * behaviour change and out of scope here.
 */
export const TABLE_CELL_BORDER_DISABLED = '#7e8190';

/** `SegmentedControl`'s divider. A 16% neutral, with no semantic counterpart. */
export const SEGMENTED_CONTROL_DIVIDER = 'rgba(32,38,45,0.16)';

/**
 * Neutral `k99` -- the palest surface step.
 *
 * Three roles share it: `Box`'s `background.muted` and `background.selectableHover`,
 * and a text input's hovered and disabled fills. It cannot be snapped to
 * `surface.neutral.background1` despite being only 4.7 away, because `Box`'s
 * `background.dim` already *is* `background1`; collapsing them would erase the
 * muted/dim distinction across ~100 call sites.
 *
 * Form inputs deliberately keep this opaque value rather than moving to an
 * `interactions.*` overlay. An input has its own opaque background at rest, and a
 * translucent `backgroundColor` on `:hover` would replace it and composite against
 * whatever is *behind* the input -- so an input sitting on a tinted card would
 * shift hue on hover. Layering (see `button/src/styles.ts`) would fix that, and is
 * the right follow-up once a form-surface token exists.
 */
export const NEUTRAL_K99_WITHOUT_TOKEN = '#f7f8f9';

/**
 * Status tints for table rows.
 *
 * Far paler than the `reserved.*.container.background` tier the status colours
 * normally use — those are for badges and notices, and are too saturated to sit
 * behind a whole row of text. A row-tint tier is the missing piece.
 *
 * `disabled` is `#d2d8ea`, whose only value match is `surface.neutral.border`; a
 * border token standing in for a row fill would assert a role it does not have.
 */
export const TABLE_ROW_TINTS = {
  cautious: '#fffaf2',
  critical: '#fdf0f1',
  positive: '#f0f9f5',
  disabled: '#d2d8ea',
} as const;

/**
 * The background of a *selected* menu item, list row or action button.
 *
 * `semantic` has no selected-surface token at all -- this is the largest genuine
 * gap left in the colour system. The three values below disagree with each other,
 * and that disagreement is itself the design question: menu items use a pale blue
 * while list rows and action buttons reuse `#d2d8ea`, whose only value match is
 * `surface.neutral.border`. A border value standing in for a selected fill is not
 * a role, so none of these can be mapped without inventing an answer.
 *
 * `Box`'s `background.selectableSelected` (`#f5faff`) and `selectableSelectedHover`
 * (`#e9f1ff`) in `./boxColor` are a fourth and fifth value for the same idea. A
 * selected-surface token should absorb all five.
 *
 * There is a behavioural question attached. When a selected item is hovered, the
 * interaction overlay currently *replaces* this background, so the selected tint
 * disappears. Compositing on top is almost certainly what's wanted, and is what
 * `button/src/styles.ts` does with its overlay layers -- but that is a behaviour
 * change, which is why these sites kept opaque values while the rest of the
 * interaction states moved to overlays.
 */
export const SURFACE_SELECTED = {
  menuItem: '#f0f7ff',
  listItem: '#d2d8ea',
  actionButton: '#d2d8ea',
} as const;

/**
 * The fill and border of a disabled checkbox or radio.
 *
 * Already an alpha overlay, and exactly equal to
 * `neutral.inverse.interactions.hover` -- but an "inverse hover" token standing in
 * for a disabled control is the wrong role, so it is held rather than mapped.
 */
export const FORM_CONTROL_SURFACE_DISABLED = '#5e5e6214';

/**
 * Icon stroke on hover and press, in `file-upload`'s remove button.
 *
 * These read `palette.button.passive.background{Focused,Pressed}` today -- a button
 * *background* token driving an icon *stroke*. They are duplicated here rather than
 * pointing at {@link DEPRECATED_BUTTON_TONE_RAMP} deliberately: the coupling is
 * accidental, and fixing the deprecated button's ramp should not silently recolour
 * an unrelated icon. What these want is an icon interaction colour.
 */
export const ICON_STROKE_HOVER = '#5f6369';
export const ICON_STROKE_PRESSED = '#44454c';

/**
 * `button-deprecated`'s bold-button hover/press ramp, indexed by tone.
 *
 * Blocked on the deprecation rather than on tokens. The current `button` package
 * already models this correctly: its base colours are byte-identical to these
 * (`primary.background` is `#5a3fff`, `reserved.critical.background` is `#c0000c`)
 * and it derives hover/press by layering `<role>.interactions.*` over them, landing
 * within 10-35 of the values below. So the fix is to migrate consumers to `button`,
 * not to add tokens for a deprecated component.
 *
 * `passive` has no clean equivalent: its base `#4e5057` is `neutral.inverse.background`,
 * but the matching `neutral.inverse.interactions.*` overlays are far too subtle on a
 * dark fill (they move it by ~1 step where palette moves it ~20), so that tone would
 * need real design input if this component were staying.
 */
export const DEPRECATED_BUTTON_TONE_RAMP = {
  critical: { focused: '#e2423a', pressed: '#e34b44' },
  active: { focused: '#6e56ff', pressed: '#6e56ff' },
  passive: { focused: '#5f6369', pressed: '#44454c' },
} as const;
